import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Radio, Play, AlertTriangle, Loader2, Clock, RefreshCw, Camera, MapPin, ChevronRight, Database, ScrollText } from "lucide-react";
import { formatGeneratedAt } from "../lib/datetime";
import { RunLogs } from "../components/RunLogs";

function statusBadge(status, used_fallback) {
  if (status === "ok" && !used_fallback) return <span className="label-mono text-bm-green">LIVE</span>;
  if (status === "ok") return <span className="label-mono text-bm-green">LIVE</span>;
  if (status === "blocked") return <span className="label-mono text-bm-danger">BLOCKED</span>;
  if (status === "error") return <span className="label-mono text-bm-danger">ERROR</span>;
  if (status === "no_data") return <span className="label-mono text-bm-amber">NO DATA</span>;
  if (status === "crashed") return <span className="label-mono text-bm-danger">CRASHED</span>;
  return <span className="label-mono text-bm-dim">{status || "—"}</span>;
}

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" });
}

export default function Ingestion() {
  const [adapters, setAdapters] = useState([]);
  const [schedule, setSchedule] = useState("");
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState({});  // slug -> bool
  const [allBusy, setAllBusy] = useState(false);
  const [tab, setTab] = useState("online");  // online | offline
  const [offlineItems, setOfflineItems] = useState([]);
  const [offlineLoading, setOfflineLoading] = useState(true);

  const reload = async () => {
    const data = await api.adapters();
    setAdapters(data.adapters);
    setSchedule(data.schedule);
    const rs = await api.ingestRuns();
    setRuns(rs.runs);
  };

  // Mount-only — reload reads only stable refs (api + setters)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);

  // Fetch offline signals when the tab becomes active (or on mount, for the badge count)
  useEffect(() => {
    if (offlineItems.length === 0) {
      api.offlineSignals({ limit: 30 }).then((d) => {
        setOfflineItems(d.items || []);
        setOfflineLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const runOne = async (slug) => {
    setBusy((b) => ({ ...b, [slug]: true }));
    try {
      await api.runIngest(slug);
      await reload();
    } finally {
      setBusy((b) => ({ ...b, [slug]: false }));
    }
  };

  const runAll = async () => {
    setAllBusy(true);
    try {
      await api.runIngest();
      await reload();
    } finally {
      setAllBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="border-b border-white/10 pb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-mono text-bm-amber flex items-center gap-2">
            <Database className="w-3.5 h-3.5" />DATA INGESTION
          </div>
          <h1 className="font-heading text-4xl font-black tracking-tight mt-1" data-testid="page-title">
            What's flowing in.
          </h1>
          <p className="text-sm text-bm-muted mt-2 max-w-3xl">
            Two streams feed the prediction engine. <span className="text-bm-cyan">Online</span> — hybrid scraper +
            (pluggable) official APIs, scheduled <span className="text-bm-amber">{schedule}</span>.
            <span className="text-bm-cyan"> Offline</span> — field-captured retail signals from scouts on the ground.
          </p>
        </div>
        {tab === "online" && (
          <button onClick={runAll} disabled={allBusy} data-testid="run-all-btn"
            className="bg-bm-amber text-black font-mono text-xs px-4 h-10 hover:bg-white transition-colors flex items-center gap-2 disabled:opacity-50">
            {allBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            INGEST ALL
          </button>
        )}
      </div>

      {/* Tab switcher */}
      <div className="flex gap-0 border-b border-white/10" data-testid="ingestion-tabs">
        <button
          onClick={() => setTab("online")}
          data-testid="tab-online"
          className={`px-4 py-2 text-xs font-mono border-b-2 transition-colors flex items-center gap-2 ${
            tab === "online"
              ? "border-bm-amber text-bm-amber"
              : "border-transparent text-bm-muted hover:text-white"
          }`}
        >
          <Radio className="w-3.5 h-3.5" />
          ONLINE ADAPTERS · {adapters.length}
        </button>
        <button
          onClick={() => setTab("offline")}
          data-testid="tab-offline"
          className={`px-4 py-2 text-xs font-mono border-b-2 transition-colors flex items-center gap-2 ${
            tab === "offline"
              ? "border-bm-amber text-bm-amber"
              : "border-transparent text-bm-muted hover:text-white"
          }`}
        >
          <Camera className="w-3.5 h-3.5" />
          OFFLINE SIGNALS · {offlineItems.length}
        </button>
        <button
          onClick={() => setTab("logs")}
          data-testid="tab-logs"
          className={`px-4 py-2 text-xs font-mono border-b-2 transition-colors flex items-center gap-2 ${
            tab === "logs"
              ? "border-bm-amber text-bm-amber"
              : "border-transparent text-bm-muted hover:text-white"
          }`}
        >
          <ScrollText className="w-3.5 h-3.5" />
          LOGS · {runs.length}
        </button>
      </div>

      {tab === "offline" ? (
        <OfflineSignalsTab items={offlineItems} loading={offlineLoading} />
      ) : tab === "logs" ? (
        <RunLogs runs={runs} onRefresh={reload} />
      ) : (
        <>
      {/* Adapter grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="adapter-grid">
        {adapters.map((a) => {
          const lr = a.last_run;
          return (
            <div key={a.slug} className="bm-card p-4 fade-up" data-testid={`adapter-${a.slug}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Radio className="w-4 h-4 text-bm-cyan" />
                  <div>
                    <div className="font-heading text-base font-semibold text-white">{a.name}</div>
                    <div className="label-mono text-bm-dim">{a.platform} · {a.geography}</div>
                  </div>
                </div>
                <button onClick={() => runOne(a.slug)} disabled={busy[a.slug]}
                  data-testid={`run-${a.slug}-btn`}
                  className="px-2 py-1 text-xs font-mono border border-bm-amber/40 text-bm-amber hover:bg-bm-amber hover:text-black transition-colors flex items-center gap-1 disabled:opacity-50">
                  {busy[a.slug] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                  RUN
                </button>
              </div>

              {a.api_mode && (
                <div className="mb-2" data-testid={`adapter-mode-${a.slug}`}>
                  <span className={`label-mono px-1.5 py-0.5 border ${
                    a.api_mode === "official_api" ? "border-bm-green/50 text-bm-green" :
                    a.api_mode === "scraper_fallback" ? "border-bm-amber/50 text-bm-amber" :
                    "border-white/15 text-bm-dim"
                  }`}>
                    {a.api_mode === "official_api" ? "OFFICIAL API ACTIVE" :
                     a.api_mode === "scraper_fallback" ? "SCRAPER · OFFICIAL API READY" :
                     "SCRAPER ONLY"}
                  </span>
                </div>
              )}
              <div className="text-xs font-mono text-bm-muted leading-relaxed break-all border-t border-white/5 pt-2">
                {a.base_url}
              </div>

              {lr ? (
                <div className="border-t border-white/5 pt-2 mt-2 space-y-1">
                  <div className="flex items-center justify-between text-xs font-mono">
                    <span className="text-bm-dim flex items-center gap-1"><Clock className="w-3 h-3" />LAST RUN</span>
                    <span className="text-bm-muted">{fmtTime(lr.finished_at)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs font-mono">
                    <span className="text-bm-dim">STATUS</span>
                    {statusBadge(lr.fetch_status, lr.used_fallback)}
                  </div>
                  <div className="flex items-center justify-between text-xs font-mono">
                    <span className="text-bm-dim">SIGNALS</span>
                    <span className="text-bm-cyan">{lr.signals_emitted}{lr.used_fallback ? " ⓘ fallback" : ""}</span>
                  </div>
                  {lr.error && (
                    <div className="text-[11px] font-mono text-bm-danger pt-1 line-clamp-2 flex items-start gap-1">
                      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                      <span>{lr.error}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="border-t border-white/5 pt-2 mt-2 text-xs font-mono text-bm-dim">
                  Never run · click RUN to ingest
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Recent runs snapshot — full detail lives in the LOGS tab */}
      <div className="bm-card p-4" data-testid="runs-log">
        <div className="flex items-center justify-between mb-3">
          <div className="label-mono text-bm-cyan flex items-center gap-2"><Clock className="w-3 h-3" />RUN HISTORY</div>
          <button onClick={() => setTab("logs")} data-testid="open-logs-tab"
            className="flex items-center gap-1 text-xs font-mono text-bm-amber hover:text-white transition-colors">
            FULL LOGS <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        <div className="space-y-1 font-mono text-xs max-h-[300px] overflow-y-auto">
          {runs.length === 0 && <div className="text-bm-muted">No runs yet. Trigger one above.</div>}
          {runs.slice(0, 12).map((r, i) => (
            <div key={`${r.finished_at || ''}-${r.adapter}-${i}`} className="flex items-center gap-3 py-1.5 border-b border-white/5 last:border-b-0">
              <span className="text-bm-dim w-32 shrink-0">{fmtTime(r.finished_at)}</span>
              <span className="text-white w-32 shrink-0">{r.adapter}</span>
              <span className="w-16 shrink-0">{statusBadge(r.fetch_status, r.used_fallback)}</span>
              <span className="text-bm-dim w-16 shrink-0">{r.trigger}</span>
              <span className="text-bm-cyan w-20 shrink-0">{r.signals_emitted} sig</span>
              <span className="text-bm-muted truncate text-[11px]">{r.error || "ok"}</span>
            </div>
          ))}
        </div>
      </div>
      </>
      )}
    </div>
  );
}


// ============ OFFLINE SIGNALS TAB ============
function OfflineSignalsTab({ items, loading }) {
  if (loading) {
    return <div className="font-mono text-xs text-bm-cyan scanline p-6">LOADING OFFLINE SIGNALS...</div>;
  }
  if (items.length === 0) {
    return <div className="text-center py-12 font-mono text-sm text-bm-muted">No offline signals captured yet.</div>;
  }
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-mono text-bm-muted">
          {items.length} field-captured retail signals · Costco, Olive Young, Don Quijote, Erewhon, Watsons, Blinkit, Nykaa, Sephora SG, H Mart, Costco Korea
        </p>
        <Link to="/offline-signals" data-testid="open-offline-fullscreen"
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono text-bm-amber border border-bm-amber/40 hover:bg-bm-amber hover:text-black transition-colors">
          OPEN FULL VIEW <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="offline-grid-inline">
        {items.slice(0, 12).map((o) => (
          <div key={o.id} className="bm-card p-3 flex flex-col gap-2" data-testid={`offline-card-${o.id}`}>
            <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono">
              <span className="px-1.5 py-0.5 border border-bm-cyan/40 text-bm-cyan">{o.retailer}</span>
              <span className="px-1.5 py-0.5 border border-bm-amber/40 text-bm-amber">{o.signal_type.replaceAll("_", " ").toUpperCase()}</span>
              <span className="text-bm-dim ml-auto">{o.observed_date}</span>
            </div>
            {o.photo_url && (
              <div className="relative aspect-[3/2] overflow-hidden bg-bm-elevated">
                <img src={o.photo_url} alt={o.observation} className="w-full h-full object-cover opacity-90" />
              </div>
            )}
            <div className="text-xs font-bold text-white leading-tight">{o.observation}</div>
            <div className="flex items-center gap-1 text-[10px] text-bm-muted">
              <MapPin className="w-2.5 h-2.5 text-bm-amber" />{o.location}
            </div>
            <div className="text-[10px] font-mono text-bm-dim flex items-center gap-1 border-t border-white/5 pt-1">
              <Clock className="w-2.5 h-2.5" />Logged {formatGeneratedAt(o.generated_at || o.created_at)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
